import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { CONSTANTS } from '@core/constants';
import { EventService, GridActions, GridService, NotificationService } from '@core/services';

@Component({
  selector: 'z-save-grid',
  templateUrl: './save.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveGridDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  saveLoading$: Observable<boolean>;
  conflictCheckLoading$: Observable<boolean>;
  form: FormGroup;

  saveId: string;
  saveName: string;

  currentSaveId: string;
  currentSaveName: string;

  display$ = new BehaviorSubject<boolean>(false);
  updateExisting$ = new BehaviorSubject<boolean>(false);

  @Input() gridId: string;

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public gridService: GridService,
    private notificationService: NotificationService,
    private translocoService: TranslocoService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    this.form = new FormGroup({
      saveName: new FormControl(this.currentSaveName, [Validators.required]),
    });

    this.saveLoading$ = this.eventService.loading(CONSTANTS.REQUEST_SAVE_VIEW);
    this.conflictCheckLoading$ = this.eventService.loading(CONSTANTS.REQUEST_SAVE_CONFLICT_CHECK);

    this.form
      .get('saveName')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.saveId = value
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-');

          this.saveName = value;
        } else {
          this.saveId = null;
          this.saveName = null;
        }

        if (this.currentSaveId === this.saveId) {
          this.updateExisting$.next(true);
        } else {
          this.updateExisting$.next(false);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit() {
    this.gridService.saveGridPanel$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === 'open') {
        this.display$.next(true);
        this.onDisplay();
      } else {
        this.display$.next(false);
      }
    });
  }

  onDisplay() {
    this.gridService
      .saveId$(this.gridId)
      .pipe(withLatestFrom(this.gridService.saveName$(this.gridId)), take(1))
      .subscribe(([saveId, saveName]) => {
        this.currentSaveId = saveId;
        this.currentSaveName = saveName;

        if (saveId && saveName) {
          this.updateExisting$.next(true);
          this.form.get('saveName').setValue(saveName);
        }

        if (this.input && this.input.nativeElement && this.input.nativeElement.focus) {
          setTimeout(() => {
            this.input.nativeElement.focus();
            this.input.nativeElement.select();
          }, 250);
        }
      });
  }

  onVisibleChange(event: any) {
    if (event === false) {
      this.close();
    }
  }

  update() {
    if (!this.saveId || !this.saveName) {
      return;
    }

    this.store.dispatch(GridActions.SaveRequest({ id: this.gridId, saveId: this.saveId, saveName: this.saveName }));
    this.close();
  }

  save() {
    if (!this.saveId || !this.saveName) {
      return;
    }

    this.store.dispatch(GridActions.SaveConflictCheckRequest({ id: this.gridId, saveId: this.saveId, saveName: this.saveName }));

    this.gridService.gridSaveConflictCheckResult$.pipe(take(1)).subscribe((data) => {
      if (data.status === 'CONFLICT') {
        this.notificationService.error({
          title: this.translate('core.components.grid.save_dialog.conflict.title'),
          message: this.translate('core.components.grid.save_dialog.conflict.message'),
        });
      } else {
        this.store.dispatch(GridActions.SaveRequest({ id: this.gridId, saveId: this.saveId, saveName: this.saveName }));
        this.close();
      }
    });
  }

  close() {
    this.form.setValue({
      saveName: null,
    });

    this.store.dispatch(GridActions.closeSaveGridPanel());
  }
}
